// #region Import
import { reactive, watch } from "vue";
import { createI18n } from 'vue-i18n';
import PortalVue from "portal-vue";
import Bottle from "bottlejs";
//Internal
import { PortalConfig } from "./classes/PortalConfig";
import { ServiceContainer } from "./factories/ServiceContainer";
import StateFactory from "./factories/StateFactory";
import ApiFactory from "./factories/ApiFactory";
import NotificationFactory from "./factories/NotificationFactory";
import LoginFactory from "./factories/LoginFactory";
import CookieFactory from "./factories/CookieFactory";
import ProviderFactory from "./factories/ProviderFactory";
import ActionFactory from "./factories/ActionFactory";
import OptionsFactory from "./factories/OptionsFactory";
import ComponentFactory from "./factories/ComponentFactory";
import MixinFactory from "./factories/MixinFactory";
import AppRouter from "./services/routing";
import MetaInfo from "./services/metadata";
//Plugin
import EventEmitter from 'promise-events';
import PluginFactory from "./factories/PluginFactory";
import CacheFactory from "./factories/CacheFactory";
// #endregion
// #region Core
export default class PortalHost {
    // #region Vars
    _app;
    _config;
    _router;
    _container;
    _states;
    _components;
    _mixins;
    _plugins;
    _localizer;
    _eventBus;
    _init;
    _services;
    _pageMeta = reactive(new MetaInfo());
    // #endregion
    // #region Vars | Public
    token = null;
    // #endregion
    // #region Properties
    get app() {
        return this._app;
    }
    get components() {
        return this._components;
    }
    get plugins() {
        return this._plugins;
    }
    get localizer() {
        return this._localizer;
    }
    get store() {
        return this._states;
    }
    get mixins() {
        return this._mixins;
    }
    get session() {
        return this._states.get("session");
    }
    get pageMeta() {
        return this._pageMeta;
    }
    get router() {
        return this._router;
    }
    get config() {
        return this._config;
    }
    get services() {
        return this._services;
    }
    get init() {
        return this._init;
    }
    get actions() {
        return this._services.get("ActionFactory");
    }
    get api() {
        return this._services.get("ApiFactory").api;
    }
    get views() {
        return this._services.get("ApiFactory").views;
    }
    get sts() {
        return this._services.get("ApiFactory").sts;
    }
    get provider() {
        return this._services.get("ProviderFactory");
    }
    get notification() {
        return this._services.get("NotificationFactory");
    }
    get cookies() {
        return this._services.get("CookieFactory");
    }
    get options() {
        return this._services.get("OptionsFactory");
    }
    get login() {
        return this._services.get("LoginFactory");
    }
    get cache() {
        return this._services.get("CacheFactory");
    }
    get eventBus() {
        return this._eventBus;
    }
    // #endregion
    // #region Constructor
    constructor(app) {
        //Vue:
        this._app = app;
        this._eventBus = new EventEmitter();
        //Default
        this._container = new Bottle("ROOT");
        this._states = new StateFactory();
        this._components = new ComponentFactory(app);
        this._plugins = new PluginFactory();
        this._mixins = new MixinFactory();
        //Services
        this._init = new ServiceContainer(this, this._container, "init");
        this._services = new ServiceContainer(this, this._container, "factories");
        //Register        
        this._services.register("ApiFactory", () => { return new ApiFactory(); });
        this._services.register("NotificationFactory", () => { return new NotificationFactory(); });
        this._services.register("LoginFactory", () => { return new LoginFactory(); });
        this._services.register("CookieFactory", () => { return new CookieFactory(); });
        this._services.register("ProviderFactory", () => { return new ProviderFactory(); });
        this._services.register("ActionFactory", () => { return new ActionFactory(); });
        this._services.register("OptionsFactory", () => { return new OptionsFactory(this); });
        this._services.register("CacheFactory", () => { return new CacheFactory(this); });
        //Watcher
        watch(() => this._pageMeta.title, (first, second) => {
            document.title = host.config.getPageTitle(first);
        });
    }
    // #endregion
    // #region Boostrap
    async start(config) {
        // #region Common
        //Config
        this._config = new PortalConfig(config);
        this._router = AppRouter;
        //Init
        this._pageMeta.init();
        //Use
        this._app.use(this.store.__store);
        this._app.use(PortalVue);
        //Components
        this.components.getComponentRegistry().forEach((component) => {
            this._app.component(component.name, component);
        });
        //Start
        this.store.commit("navigation/startup", this);
        this.store.commit("session/startup", this);
        this.store.commit("carts/startup", this);
        this.store.commit("windows/startup", this);
        // #endregion
        // #region Plugin: Startup
        this.plugins.runStartUp();
        // #endregion
        //#region Login
        await this.login.loginIfHasCoockie();
        // #endregion
        // #region Start
        await this.api.get("system/app/boostrap").then(e => {
            // #region Routes
            if (e.data.routes) {
                e.data.routes.forEach((e) => {
                    if (AppRouter.hasRoute(e.name)) {
                        return;
                    }
                    AppRouter.addRoute(e);
                });
            }
            // #endregion
            // #region Translations
            if (e.data.translations) {
                //vars
                let messages = e.data.translations;
                //i18n
                let tLocal = createI18n({
                    legacy: true,
                    locale: navigator.language.split('-')[0],
                    fallbackLocale: ['de'],
                    messages
                });
                this._app.use(tLocal);
                this._localizer = tLocal.global;
            }
            else {
                //vars
                let messages = {};
                //i18n
                let tLocal = createI18n({
                    legacy: true,
                    locale: navigator.language.split('-')[0],
                    fallbackLocale: ['de'],
                    messages
                });
                this._app.use(tLocal);
                this._localizer = tLocal.global;
            }
            // #endregion
            // #region Navigation
            if (e.data.navigation) {
                this.store.commit('navigation/setMenu', e.data.navigation);
            }
            // #endregion
            // #region Banner
            if (e.data.banner) {
                this.store.commit('navigation/setBanner', e.data.banner);
            }
            // #endregion
            // #region Plugin: Startup
            this.plugins.runBoostrap(e.data);
            // #endregion
        });
        // #endregion      
        // #region App
        //Modules
        this._app.use(AppRouter);
        //Mount
        this._app.mount('#app');
        // #endregion
    }
}
// #endregion
